// AI-CONTEXT: See docs/features/subscriptions-overview-page.md
//
// De standen van het abonnementenoverzicht, met hun label en hun volgorde.
//
// De waarden zijn dezelfde als DISPLAY_STATUS en MANUAL_STATUS in
// functions/subscriptionsOverview/resolveSubscriptionStatus.js. Dat is een
// spiegel, en die wordt hier niet met een comment bewaakt maar met
// subscriptionStatus.test.js, dat het échte bestand uit functions/ inleest.
// AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment

/** Wat de server als 'geen handmatige stand' leest. */
const AUTOMATIC = ''

/**
 * @typedef {object} StatusOption
 * @property {string} value - de waarde zoals de server hem kent
 * @property {string} label - wat de gebruiker leest
 */

/**
 * Alle standen die in de kolom en op de tabbladen kunnen staan, in werkvolgorde:
 * eerst waar iets moet gebeuren, dan wat loopt, dan wat is vastgelegd. Dezelfde
 * volgorde als sortSubscriptionRows op de server, zodat de tabbladen van links
 * naar rechts hetzelfde verhaal vertellen als de lijst van boven naar beneden.
 *
 * @type {Array<StatusOption>}
 */
const STATUS_OPTIONS = [
    { value: 'inactive', label: 'Betaling inactief' },
    { value: 'expired', label: 'Verlopen' },
    { value: 'trial', label: 'Proefperiode' },
    { value: 'active', label: 'Betaling actief' },
    { value: 'free', label: 'Gratis account' },
    { value: 'pilot', label: 'Onderdeel van pilot' },
    { value: 'portfolio', label: 'Valt onder portfolio' },
    { value: 'stopped', label: 'Gestopt' },
    { value: 'none', label: 'Geen status' },
]

/**
 * De keuzes in het menu per rij, in dezelfde volgorde als de tabbladen.
 *
 * 'Verlopen' staat er bewust NIET in: die volgt recht uit de einddatum in
 * dezelfde rij. 'Betaling inactief' en 'Betaling actief' staan er wél in — een
 * beheerder kan wéten dat een betaling stilligt, of een betaling hebben
 * toegevoegd terwijl de periode nog niet compleet is.
 *
 * @type {Array<StatusOption>}
 */
const MANUAL_STATUS_OPTIONS = [
    // Kort: het label erboven laat al zien waar 'automatisch' op uitkomt, en
    // een lange keuze maakt het menu breder dan de kolom.
    { value: AUTOMATIC, label: 'Automatisch' },
    { value: 'inactive', label: 'Betaling inactief' },
    { value: 'trial', label: 'Proefperiode' },
    { value: 'active', label: 'Betaling actief' },
    { value: 'free', label: 'Gratis account' },
    { value: 'pilot', label: 'Onderdeel van pilot' },
    { value: 'portfolio', label: 'Valt onder portfolio' },
    { value: 'stopped', label: 'Gestopt' },
    // Niet hetzelfde als 'Automatisch' hierboven: die laat de betaalgegevens
    // spreken en zet de rij dus in 'betaling inactief' of 'betaling actief'.
    // Dit is een bewuste keuze die niets over de betaling beweert.
    { value: 'none', label: 'Geen status' },
]

/**
 * @param {string} value - een stand zoals de server hem stuurt
 * @returns {string} het label, of de waarde zelf als die hier nog niet bekend is
 */
const statusLabel = (value) => {
    const gevonden = STATUS_OPTIONS.find((o) => o.value === value)
    // Niet leeg teruggeven: een lege cel leest als "geen status", terwijl er
    // juist iets staat dat we hier nog niet kennen.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    return gevonden ? gevonden.label : value
}

export { STATUS_OPTIONS, MANUAL_STATUS_OPTIONS, AUTOMATIC, statusLabel }
